/*
 * Client-side hook to resolve SEO URLs to category IDs
 * Resolves categoryId from SEO URL parameters when not in SSR
 */

import {useQuery} from '@tanstack/react-query'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useSsrFetchOrigin} from './use-ssr-fetch-origin'

import {getLocaleID} from '../utils/siteUtils'

/**
 * Resolves SEO URL segment to category ID using Commerce Cloud Shopper SEO API
 */
async function resolveCategoryId(locale, brandId, categoryPath, accessToken, appOrigin) {
    const urlSegment = `${brandId}/${categoryPath}`
    const config = getConfig()

    // Use same configuration as SSR
    const proxyPath = config.app.commerceAPI.proxyPath
    const organizationId = config.app.commerceAPI.parameters.organizationId
    const siteId = config.app.commerceAPI.parameters.siteId

    const localeId = getLocaleID(siteId, locale)

    let url = `${appOrigin}${proxyPath}/site/shopper-seo/v1/organizations/${organizationId}/url-mapping?siteId=${siteId}&urlSegment=${encodeURIComponent(urlSegment)}`
    if (localeId) {
        url = `${appOrigin}${proxyPath}/site/shopper-seo/v1/organizations/${organizationId}/url-mapping?siteId=${siteId}&locale=${localeId}&urlSegment=${encodeURIComponent(urlSegment)}`
    }

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`
        }
    })

    if (!response.ok) {
        throw new Error(`Failed to resolve SEO URL: ${response.status}`)
    }

    const data = await response.json()

    if (data.resourceType === 'category' && data.resourceId) {
        return data.resourceId
    }

    return null
}

/**
 * Hook to resolve categoryId from SEO URL parameters
 * Only runs when we have SEO URL params and no server-side data
 */
export const useClientCategoryId = (
    locale,
    brandId,
    categoryPath,
    skipClientResolve = false
) => {
    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = useSsrFetchOrigin()
    const shouldRun = !!(brandId && categoryPath && !skipClientResolve)

    return useQuery({
        queryKey: ['client-category-id', locale, brandId, categoryPath],
        queryFn: async () => {
            const accessToken = await getTokenWhenReady()
            return resolveCategoryId(locale, brandId, categoryPath, accessToken, appOrigin)
        },
        enabled: shouldRun,
        staleTime: 0, // No cache - always fresh request
        cacheTime: 0,
        retry: 1
    })
}
